// Two-tone notification chime for incoming task notifications.
//
// Uses the Web Audio API to synthesize a soft A5 -> E6 "ding" so we don't
// have to ship an audio asset. Honours the `notif:sound:enabled` localStorage
// preference; the bell UI toggles this and previews the chime on enable.

const PREF_KEY = 'notif:sound:enabled';

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext;
}

let _audioCtx: AudioContext | null = null;
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (_audioCtx) return _audioCtx;
  try {
    const w = window as WindowWithWebkitAudio;
    const Ctx = w.AudioContext || w.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
  } catch {
    return null;
  }
  return _audioCtx;
}

let _audioUnlocked = false;
export function unlockAudioOnGesture(): void {
  if (_audioUnlocked || typeof window === 'undefined') return;
  const handler = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
    _audioUnlocked = true;
    window.removeEventListener('click', handler);
    window.removeEventListener('keydown', handler);
    window.removeEventListener('touchstart', handler);
  };
  window.addEventListener('click', handler, { once: true });
  window.addEventListener('keydown', handler, { once: true });
  window.addEventListener('touchstart', handler, { once: true });
}

function playPing(): void {
  if (typeof window === 'undefined') return;
  if (window.localStorage.getItem(PREF_KEY) === '0') return;
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => undefined);
  }
  try {
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);

    const tone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(1, now + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
      osc.connect(gain).connect(master);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.02);
    };

    tone(880, 0, 0.18);
    tone(1318.5, 0.12, 0.22);
  } catch {
    /* ignore audio failures */
  }
}

export function playNotificationSound(): void {
  playPing();
}

export function getSoundEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(PREF_KEY) !== '0';
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PREF_KEY, enabled ? '1' : '0');
}

export function previewNotificationSound(): void {
  const prev = typeof window !== 'undefined' ? window.localStorage.getItem(PREF_KEY) : null;
  if (prev === '0') window.localStorage.setItem(PREF_KEY, '1');
  playPing();
  if (prev === '0') window.localStorage.setItem(PREF_KEY, '0');
}
